import React, {useLayoutEffect, useRef} from "react";
import {connect} from "react-redux";
import {Admission} from "@reside/reside-api-admission";
import {Box, Button, styled} from "@reside/ui";

import {AppState, select} from "../../store";
import {Spinner} from "../spinner";
import {ColumnBody, ColumnCardsPadding, ColumnHeader} from "./Column";
import {AdmissionCellList} from "./AdmissionCellList";
import {
  useAdmissionsByStatusQuery,
  useStickyColumnHeaderDivider,
} from "./hooks";
import {VIEW_MODE} from "./constants";
import {readableAdmissionStatus} from "../admission-status-tag";
import {requestDashboardGridLayoutSync} from "./dashboardGridLayoutSync";

const plural = (
  totalElements: number,
  zero: string,
  one: string,
  other: string,
) => {
  switch (totalElements) {
    case 0:
      return zero;
    case 1:
      return `${totalElements} ${one}`;
    default:
      return `${totalElements} ${other}`;
  }
};

const Renderer = ({
  status,
  facilityIds,
  loadMoreButtonId,
  starredOnly,
  workflowType,
}: {
  status: Admission.StatusEnum;
  loadMoreButtonId: string;
  facilityIds: ReadonlyArray<string> | null;
  starredOnly?: boolean;
  workflowType?: string | null;
}) => {
  const {data, hasNextPage, fetchNextPage, isFetching} =
    useAdmissionsByStatusQuery({
      facilityIds,
      status,
      starredOnly,
      viewMode: VIEW_MODE.GRID,
      workflowType,
    });

  const readableStatus = readableAdmissionStatus(status);
  const [{totalElements = 0} = {}] = data?.pages ?? [];

  const headerRef = useRef<HTMLElement>(null);
  const cardsPaddingRef = useRef<HTMLDivElement>(null);
  const headerDividerActive = useStickyColumnHeaderDivider(
    headerRef,
    cardsPaddingRef,
  );

  const admissionCount = facilityIds?.length
    ? (data?.pages.flatMap(item => item.content).length ?? 0)
    : 0;

  useLayoutEffect(() => {
    requestDashboardGridLayoutSync();
  }, [admissionCount, data?.pages.length, isFetching]);

  return (
    <ColumnBody data-test-id={`${readableStatus.toLowerCase()}-column`}>
      <ColumnHeader
        ref={headerRef}
        scrolledUnder={headerDividerActive}
        title={readableStatus}
        subtitle={plural(
          facilityIds?.length ? totalElements : 0,
          "NO ADMISSIONS",
          "ADMISSION",
          "ADMISSIONS",
        )}
      />
      <ColumnCardsPadding ref={cardsPaddingRef}>
        {data && (
          <AdmissionCellList
            admissions={
              facilityIds?.length
                ? data.pages.flatMap(item => item.content)
                : []
            }
          />
        )}

        <Box flexDirection="row" justifyContent="space-around">
          {hasNextPage && (facilityIds?.length ?? 0) > 0 && !isFetching && (
            <Button
              size="small"
              color="primary"
              onClick={() => {
                fetchNextPage();
              }}
              testId={loadMoreButtonId}
            >
              Load More
            </Button>
          )}
          {isFetching && (
            <Offset>
              <Spinner relative />
            </Offset>
          )}
        </Box>
      </ColumnCardsPadding>
    </ColumnBody>
  );
};

const mapStateToProps = (state: AppState) => ({
  facilityIds: select.adminSession.activeFacilitiesIds(state),
});

export const DashboardGridColumn = connect(mapStateToProps)(Renderer);

const Offset = styled.div`
  margin-top: ${({theme}) => theme.space[6]}px;
`;
